import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getDataColor } from '../../../styles/dataColors.js';

function getInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export default function TechniciansAvatarGroup({
  technicians = [],
  maxVisible = 4,
  showCount = true,
  emptyText = 'Sin tecnicos activos',
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const closeTimeoutRef = useRef(null);
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleCloseTooltip = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setActiveTooltip(null);
    }, 140);
  };

  const openTooltip = (tech, event) => {
    clearCloseTimeout();

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 260;
    const tooltipHeight = 110;
    const margin = 12;

    let left = rect.left + (rect.width / 2);
    let top = rect.top - 8;
    let placement = 'top';

    if ((rect.top - tooltipHeight) < margin) {
      top = rect.bottom + 8;
      placement = 'bottom';
    }

    const minCenterX = margin + (tooltipWidth / 2);
    const maxCenterX = window.innerWidth - margin - (tooltipWidth / 2);
    left = Math.max(minCenterX, Math.min(left, maxCenterX));

    setActiveTooltip({
      tech,
      left,
      top,
      placement,
    });
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  if (!Array.isArray(technicians) || technicians.length === 0) {
    return <span className="text-xs text-content-muted">{emptyText}</span>;
  }

  const visible = technicians.slice(0, maxVisible);
  const hiddenCount = Math.max(technicians.length - visible.length, 0);

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        {visible.map((tech, index) => (
          <div
            key={`${tech.email || tech.name || 'tech'}-${index}`}
            className="relative"
            style={{ marginLeft: index === 0 ? 0 : -8, zIndex: visible.length - index }}
            onMouseEnter={(event) => openTooltip(tech, event)}
            onMouseLeave={scheduleCloseTooltip}
          >
            <span
              className="w-8 h-8 rounded-full border-2 border-bg text-[11px] font-bold text-content inline-flex items-center justify-center"
              style={{ backgroundColor: getDataColor(index) }}
            >
              {getInitials(tech.name)}
            </span>
          </div>
        ))}

        {hiddenCount > 0 && (
          <span className="w-8 h-8 rounded-full border-2 border-bg bg-surface-2 text-[11px] font-bold text-content inline-flex items-center justify-center" style={{ marginLeft: -8 }}>
            +{hiddenCount}
          </span>
        )}
      </div>

      

      {portalTarget && activeTooltip && createPortal(
        <div
          className="fixed z-[12000] w-64 rounded-lg border border-line bg-surface p-2 shadow-xl"
          style={{
            left: activeTooltip.left,
            top: activeTooltip.top,
            transform: activeTooltip.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          }}
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={scheduleCloseTooltip}
        >
          <div className="text-content text-sm font-semibold break-words select-text">{activeTooltip.tech.name || 'Tecnico'}</div>
          <div className="text-content-muted text-xs break-all select-text">{activeTooltip.tech.email || 'Sin correo'}</div>
          <div className="text-muted-deep text-xs break-words select-text">{activeTooltip.tech.phone || 'Sin telefono'}</div>
        </div>,
        portalTarget,
      )}
    </div>
  );
}
